import React, { useEffect, useCallback } from 'react';
import { usePlatformAuth } from '../contexts/AuthContext';
import { Account, UserRequest, Patient } from '../types';

import { api } from '../services/api';
import { useUploaderState, useTemplateManagement, useDocumentGeneration, useFileManagement } from '../hooks';
import { StepBasedUploader, UploaderModals } from './uploader';
import { RequestAnimationCard } from './RequestAnimationCard';

import { InputSummary } from './InputSummary';
import { SettingsPage } from './SettingsPage';
import { LoadingScreen } from './LoadingScreen';

interface UploaderPageProps {
  account: Account;
  onNewRequest?: (request: UserRequest | null) => void;
}

export const UploaderPage: React.FC<UploaderPageProps> = ({ 
  account, 
  onNewRequest
}) => {
  const auth = usePlatformAuth();
  
  // Use custom hooks for state management
  const {
    currentAccount,
    setCurrentAccount,
    templates,
    setTemplates,
    selectedTemplate,
    setSelectedTemplate,
    attachments,
    setAttachments,
    isLoading,
    setIsLoading,
    error,
    setError,
    setFinalResult,

    setCurrentStage,
    isProcessingComplete,
    setIsProcessingComplete,
    isManageTemplatesModalVisible,
    setIsManageTemplatesModalVisible,
    showSettings,
    setShowSettings,
    showTemplateSettingsModal,
    setShowTemplateSettingsModal,
  } = useUploaderState(account);

  // Add state for add template modal
  const [showAddTemplateModal, setShowAddTemplateModal] = React.useState(false);

  // Add state for selected patient and patient data
  const [selectedPatient, setSelectedPatient] = React.useState('');
  const [patients, setPatients] = React.useState<Patient[]>([]);
  const [refreshPatientsTrigger, setRefreshPatientsTrigger] = React.useState(0); // Local state for patient refresh

  // Add state for patient modal
  const [showPatientModal, setShowPatientModal] = React.useState(false);

  // Add state for request animation
  const [showRequestAnimation, setShowRequestAnimation] = React.useState(false);
  const [animatingRequest, setAnimatingRequest] = React.useState<UserRequest | null>(null);
  const [shouldResetStep, setShouldResetStep] = React.useState(false);
  const [isFormShrinking, setIsFormShrinking] = React.useState(false);

  // Use custom hooks for business logic
  const { handleTemplateAdded, fetchTemplates } = useTemplateManagement(
    currentAccount,
    setCurrentAccount,
    setTemplates
  );

  const { handleGenerate } = useDocumentGeneration(
    selectedTemplate,
    templates,
    attachments,
    currentAccount,
    selectedPatient,
    setIsLoading,
    setCurrentStage,
    setError,
    setFinalResult,
    setIsProcessingComplete,
    (newRequest) => {
      
      // Set patient name for the request animation - CRITICAL: ensure we have this
      const patientName = selectedPatient ? getPatientNameById(selectedPatient) : 'No Patient Selected';
      newRequest.patientName = patientName;

      
      // Start the animation
      setAnimatingRequest(newRequest);
      setShowRequestAnimation(true);
    }
  );


  const handleFormShrinkStart = useCallback(() => {
    setIsFormShrinking(true);
  }, []);

  const handleAnimationComplete = useCallback(() => {
    // Animation is done, now update the parent and reset the form
    if (onNewRequest && animatingRequest) {
      onNewRequest(animatingRequest);
    }
    
    // Now it's safe to stop the loading state and reset everything
    setIsLoading(false);
    setCurrentStage(1);
    
    // Reset the uploader state to show a fresh uploader
    setFinalResult(null);
    setAttachments([]);
    // Keep the same template selected - this ensures selectedTemplateData remains valid
    // setSelectedTemplate(selectedTemplate); // This line is redundant since we're not changing it
    setSelectedPatient(''); // Clear patient selection to force intentional selection for next request
    setError(null);
    setIsProcessingComplete(false);
    
    // Reset the step back to 1
    setShouldResetStep(true);
    
    // Hide the animation and reset form shrinking
    setShowRequestAnimation(false);
    setAnimatingRequest(null);
    setIsFormShrinking(false);
    
    // Reset the step flag after a brief moment
    setTimeout(() => setShouldResetStep(false), 100);
  }, [onNewRequest, animatingRequest, selectedTemplate, setIsLoading, setCurrentStage, setFinalResult, setAttachments, setSelectedTemplate, setSelectedPatient, setError, setIsProcessingComplete]);

  const { handleFilesAdded, handleRemoveAttachment, scrollToBottom } = useFileManagement(setAttachments);

  // Helper function to get patient name from ID
  const getPatientNameById = useCallback((patientId: string): string => {
    const patient = patients.find(p => p.id === patientId);
    return patient ? patient.name : patientId; // Fallback to ID if not found
  }, [patients]);

  // Fetch templates on mount
  useEffect(() => {
    if (!auth.isAuthenticated || !currentAccount) return;
    const loadTemplates = async () => {
      try {
        await fetchTemplates();
      } catch (err) {
        setError('Failed to load templates. Please try again.');
        console.error('Error fetching templates:', err);
      }
    };
    loadTemplates();
  }, [auth.isAuthenticated, currentAccount, fetchTemplates, setError]);

  // Fetch patients on mount
  useEffect(() => {
    if (!auth.isAuthenticated || !currentAccount) return;
    const loadPatients = async () => {
      try {
        const patientsData = await api.getPatients();
        setPatients(patientsData);
      } catch (err) {
        console.error('Error fetching patients:', err);
        // Don't show error to user since this is not critical for the main flow
      }
    };
    loadPatients();
  }, [auth.isAuthenticated, currentAccount, setRefreshPatientsTrigger]); // Add refreshPatientsTrigger to dependency array

  // Event handlers
  const handleProcessAnother = useCallback(() => {
    setIsProcessingComplete(false);
    setFinalResult(null);
    setAttachments([]);
    // Don't clear the selectedTemplate - keep the current selection
    // setSelectedTemplate('');
    setError(null);
  }, [setIsProcessingComplete, setFinalResult, setAttachments, setError]);



  const handleManageTemplatesClose = useCallback(() => {
    setIsManageTemplatesModalVisible(false);
  }, [setIsManageTemplatesModalVisible]);



  const handleTemplateSettingsClick = useCallback(() => {
    setShowTemplateSettingsModal(true);
  }, [setShowTemplateSettingsModal]);

  const handleAddTemplateClick = useCallback(() => {
    setShowAddTemplateModal(true);
  }, [setShowAddTemplateModal]);

  const handleTemplateSettingsClose = useCallback(() => {
    setShowTemplateSettingsModal(false);
  }, [setShowTemplateSettingsModal]);

  const handlePatientModalShow = useCallback(() => {
    setShowPatientModal(true);
  }, []);

  const handlePatientModalClose = useCallback(() => {
    setShowPatientModal(false);
  }, []);

  const handlePatientAdded = useCallback((newPatient: Patient) => {
    setPatients(prev => [newPatient, ...prev]);
    setSelectedPatient(newPatient.id);
    setRefreshPatientsTrigger(prev => prev + 1); // Increment to trigger refresh
  }, [setPatients, setSelectedPatient, setRefreshPatientsTrigger]);

  // Handler for when recording is completed - creates request animation
  const handleRecordingComplete = useCallback((userRequestId: string) => {
    // Create the animation card with the data we already have - no API calls needed
    const patientName = selectedPatient ? getPatientNameById(selectedPatient) : 'No Patient Selected';
    const templateName = templates.find(t => t.id === selectedTemplate)?.name;
    
    const animationUserRequest: UserRequest = {
      id: userRequestId,
      userId: '', // Not needed for animation
      accountId: '', // Not needed for animation
      patientId: selectedPatient,
      patientName: patientName,
      taskDefinitionId: selectedTemplate,
      taskType: '', // Not needed for animation
      taskName: templateName || 'NOT SET', // Ensure taskName is always a string
      status: 'Pending' as any, // Cast to avoid type issues
      createdAt: new Date().toISOString(),
      generatedDocumentS3Keys: [],
      generatedNotes: [],
      processingContextId: '',
      logs: []
    };
    
    // Start the animation immediately
    setAnimatingRequest(animationUserRequest);
    setShowRequestAnimation(true);
  }, [selectedPatient, getPatientNameById, selectedTemplate, templates]);


  const handleSettingsBack = useCallback(() => {
    setShowSettings(false);
  }, [setShowSettings]);

  // Computed values
  const isGenerateDisabled = !selectedTemplate || attachments.length === 0 || !currentAccount;
  const selectedTemplateName = templates.find(t => t.id === selectedTemplate)?.name || 'Unknown Template';

  // Loading and auth gating
  if (auth.isLoading) return <LoadingScreen />;
  if (!auth.isAuthenticated) return <LoadingScreen message="Redirecting to login" />;
  if (!currentAccount) return <LoadingScreen />;



  // Show settings page if requested
  if (showSettings) {
    return <SettingsPage 
      onBack={handleSettingsBack} 
      account={currentAccount}
      onAccountUpdate={setCurrentAccount}
    />;
  }

  return (
    <div className="uploader-page">
      {/* Back Navigation - Only show when processing is complete */}
      {isProcessingComplete && (
        <div className="p-4">
          <button 
            onClick={handleProcessAnother}
            className="btn btn-outline"
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
            </svg>
            BACK TO UPLOAD PAGE
          </button>
        </div>
      )}

      <main className="uploader-page__main">
        {error && (
          <div className="uploader-page__error">
            {error}
            <button 
              onClick={() => setError(null)}
              className="uploader-page__error-close"
            >
              ×
            </button>
          </div>
        )}

        <div className="uploader-page__content">
          {/* Input Area - Collapsed when processing is complete */}
          {!isProcessingComplete ? (
            <div 
              className={`transition-all duration-200 ease-out transform ${
                isFormShrinking ? 'opacity-50' : 'scale-100 opacity-100'
              }`}
              style={{
                transform: isFormShrinking ? 'scale(0.3)' : 'scale(1)'
              }}
            >
              <StepBasedUploader
                templates={templates}
                selectedTemplate={selectedTemplate}
                onTemplateChange={setSelectedTemplate}
                onTemplateSettingsClick={handleTemplateSettingsClick}
                onAddTemplateClick={handleAddTemplateClick}
                onFilesAdded={handleFilesAdded}
                attachments={attachments}
                onRemoveAttachment={handleRemoveAttachment}
                onGenerate={handleGenerate}
                isGenerateDisabled={isGenerateDisabled}
                isLoading={isLoading}
                selectedPatient={selectedPatient}
                onPatientChange={setSelectedPatient}
                onShowAddModal={handlePatientModalShow}
                getPatientNameById={getPatientNameById}
                shouldResetToStep1={shouldResetStep}
                refreshPatientsTrigger={refreshPatientsTrigger} // Use local state
                scrollToBottom={scrollToBottom}
                account={currentAccount}
                onRecordingComplete={handleRecordingComplete}
              />
            </div>
          ) : (
            <InputSummary
              attachments={attachments}
              selectedTemplateName={selectedTemplateName}
              selectedPatient={selectedPatient ? getPatientNameById(selectedPatient) : ''}
              onProcessAnother={handleProcessAnother}
            />
          )}
        </div>
      </main>

      {/* Modals */}
      <UploaderModals
        isManageTemplatesModalVisible={isManageTemplatesModalVisible}
        onManageTemplatesClose={handleManageTemplatesClose}
        onTemplateAdded={handleTemplateAdded}
        currentAccount={currentAccount}
        onAccountUpdated={setCurrentAccount}
        showTemplateSettingsModal={showTemplateSettingsModal}
        onTemplateSettingsClose={handleTemplateSettingsClose}
        selectedTemplate={selectedTemplate}
        templates={templates}
        showAddTemplateModal={showAddTemplateModal}
        onAddTemplateClose={() => setShowAddTemplateModal(false)}
        showPatientModal={showPatientModal}
        onPatientModalClose={handlePatientModalClose}
        onPatientAdded={handlePatientAdded}
      />

      {/* Request Animation */}
      <RequestAnimationCard
        isVisible={showRequestAnimation}
        request={animatingRequest}
        onAnimationComplete={handleAnimationComplete}
        onFormShrinkStart={handleFormShrinkStart}
      />
    </div>
  );
}; 